import { FsOptions, fsService } from '@services/firebase/firestore/firestore';
import { log } from '@utils/log';
import { last } from 'es-toolkit';
import { limit as fsLimit, orderBy, QueryConstraint, startAt } from 'firebase/firestore';
import {
  ApiRequestOptions,
  apiService,
  QueryConstraintModified
} from 'src/backend-api/default.api';

type NewDoc = {
  id: string;
  created_at: Date;
};

export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

export class InitialDataService {
  static async loadFromServer<T>(
    collection: string,
    filter: QueryConstraintModified = {},
    options: ApiRequestOptions = {},
    updateStoreData: (data: T[]) => void,
    batchSize?: number
  ) {
    let compoundedData: (T & NewDoc)[] = [];
    let first = true;
    let limit = 50;
    let lastRecord: T & NewDoc;
    const sortOrder = ['created_at:desc'];

    try {
      const count = await apiService.count(collection, filter, {
        orderBy: sortOrder
      });

      if (count === 0) {
        updateStoreData([]);
        return;
      }

      const next = async () => {
        const resumeAfter = {
          ...(!first && { startAt: lastRecord.id })
        };

        const data = await apiService.getDocs<T & NewDoc>(collection, filter, {
          orderBy: sortOrder,
          ...resumeAfter,
          ...options,
          limit
        });

        first = false;

        if (data.length) {
          limit = batchSize || limit + 200;
        }

        // deduplicate and append data
        const uniqueData = [...compoundedData];
        data.forEach((d) => {
          if (!uniqueData.some((existing) => existing.id === d.id)) {
            uniqueData.push(d);
          }
        });
        compoundedData = uniqueData;

        // set updated data in local store
        updateStoreData(compoundedData);

        log.info(
          `LoadInitData: Fetched ${compoundedData.length}/${count} records for ${collection}`
        );

        if (compoundedData.length < count && data.length) {
          const validLast = last(data);
          if (!validLast) {
            log.error(
              `LoadInitData: No valid last record found for ${collection}. Stopping pagination.`
            );
            return;
          }
          lastRecord = validLast;
          await next();
        }
      };

      await next();
    } catch (error) {
      log.error(`LoadInitData: Error loading data for ${collection}:`, error);
      updateStoreData(compoundedData);
    }
  }

  static uniqueItemsSortedByCreatedAt<T extends NewDoc>() {
    return (items: T[]): T[] => {
      // Using "as T" to maintain the original type
      return Array.from(new Map(items.map((item) => [item.id, item])).values()).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ) as T[];
    };
  }

  static async load<T>(
    collection: string,
    filter: QueryConstraint[] = [],
    options: FsOptions<T & NewDoc> = {},
    onDataUpdate: (data: (T & NewDoc)[]) => void,
    batchQuery?: [string, 'in' | 'array-contains-any', string[]] | null
  ) {
    let compoundedData: (T & NewDoc)[] = [];
    let first = true;
    let limit = 100;
    let lastRecord: T & NewDoc;
    let count = 0;

    options = options || {};

    try {
      if (!batchQuery) {
        count = (await fsService.getCount(collection, filter, options)) ?? 0;
      } else {
        count = (await fsService.getCountInBatches(collection, batchQuery, filter, options)) ?? 0;
      }

      if (count === 0) {
        onDataUpdate([]);
        return [];
      }

      // Helper function to find the last record with a valid created_at property.
      const findLastValidRecord = <DataRecord extends { id: string; created_at?: Date }>(
        records: DataRecord[]
      ): DataRecord | null => {
        for (let i = records.length - 1; i >= 0; i--) {
          if (records[i].created_at) {
            return records[i];
          }
        }
        return null;
      };

      const next = async () => {
        let newFilter = [...filter];
        newFilter = newFilter.concat(orderBy('created_at', 'desc'));

        // then, if not the first call, add the startAt constraint using the field from the last valid record
        if (!first) {
          if (!lastRecord?.created_at) {
            log.error('Last record does not contain created_at. Skipping invalid record.');
            // Find a valid record from compoundedData to use as reference.
            const validRecord = findLastValidRecord(compoundedData);
            if (validRecord && validRecord.created_at) {
              newFilter = newFilter.concat(startAt(validRecord.created_at));
            } else {
              log.error('No valid record with created_at found. Stopping pagination.');
              // Only update data, don't mark as complete since this is an error
              onDataUpdate(compoundedData);
              return;
            }
          } else {
            newFilter = newFilter.concat(startAt(lastRecord.created_at));
          }
        }

        // append the limit at the end
        newFilter = newFilter.concat(fsLimit(limit));

        let data: (T & NewDoc)[] = [];

        if (!batchQuery) {
          data = await fsService.getDocs<T & NewDoc>(collection, newFilter, options);
        } else {
          // For batch queries, we need to wait for all batches
          // and ensure incremental updates happen through the updateBatchData function
          const batches = await fsService.getDocsInBatches<T & NewDoc>(
            collection,
            batchQuery,
            newFilter,
            (incrementalData) => {
              // Deduplicate and merge with existing data from previous batches
              incrementalData.forEach((item) => {
                if (!compoundedData.some((existing) => existing.id === item.id)) {
                  compoundedData.push(item);
                }
              });

              // Update UI with merged data
              onDataUpdate(compoundedData);
            },
            options
          );

          // All batches have already been merged into compoundedData via the callback
          data = batches.reduce((acc, batch) => [...acc, ...batch], []);
        }

        first = false;

        const pageLimit = limit;
        limit = 500;

        // append data to our accumulated set
        const uniqueData = [...compoundedData];

        data.forEach((d) => {
          if (!uniqueData.some((existing) => existing.id === d.id)) {
            uniqueData.push(d);
          }
        });
        compoundedData = uniqueData;

        // set updated data in local store
        onDataUpdate(compoundedData);

        if (data.length === pageLimit) {
          // Use helper to find the last valid record in this batch (skipping any invalid ones)
          const validLastRecord = findLastValidRecord(data);
          if (validLastRecord && validLastRecord.created_at) {
            log.info(
              `Next page for ${collection}, last record created_at: ${validLastRecord.created_at.toISOString()}`
            );
            lastRecord = validLastRecord;
            await next();
          } else {
            log.error('No valid record with created_at found in the latest batch.');
            // Don't mark as complete since this is an error condition
            return;
          }
        } else {
          // Only mark as complete when we're actually done
          return;
        }
      };

      await next();

      return compoundedData;
    } catch (error) {
      log.error(`Error loading data for ${collection}:`, error);
      // Update the UI with partial data
      onDataUpdate(compoundedData);
    }
  }
}
